import * as angular from 'angular';
import { Decimal } from 'decimal.js';
import * as moment from 'pkp-moment-timezone-all-years';
import { DateIntervalAsMoment } from '../custom';

type RuntimePreSelection = 'MONTH' | 'DAY';

/**
 * Multi-purpose service meant for all things general.
 */
export class UtilService {
    constructor(private $timeout: ng.ITimeoutService) {}

    /**
     * @param [numberOfSegments=5] A GUID consists of five segments split by a hyphen.
     *  Setting this you can limit the number of segments returned. Setting >5 equals 5.
     */
    public generateGuid(numberOfSegments: number = 5): string {
        let segmentDelimiter: string = '-';

        function s4() {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        }

        let guid: string =
            s4() +
            s4() +
            segmentDelimiter +
            s4() +
            segmentDelimiter +
            s4() +
            segmentDelimiter +
            s4() +
            segmentDelimiter +
            s4() +
            s4() +
            s4();
        let segments: string[] = guid.split(segmentDelimiter);

        return segments.slice(0, numberOfSegments).join(segmentDelimiter);
    }

    public values<T>(obj: Object): T[] {
        return !obj ? [] : Object.keys(obj).map((key) => obj[key]);
    }

    /**
     * @see http://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
     */
    public shuffle<T>(array: T[]): T[] {
        let currentIndex = array.length,
            temporaryValue,
            randomIndex;

        while (0 !== currentIndex) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;

            temporaryValue = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temporaryValue;
        }

        return array;
    }

    /**
     * Remove all elements whose keys start with an underscore <tt>_</tt> or dollar <tt>$</tt>.
     * @todo Recursion for deep removal and unit test.
     */
    public sanitize<
        T extends { [key: string]: any },
        U extends { [key: string]: any }
    >(objectLiteral: T): U {
        return Object.keys(objectLiteral)
            .filter((key) => !key.startsWith('_') && !key.startsWith('$'))
            .reduce((previousValue, currentValue, currentIndex, array) => {
                previousValue[currentValue] = objectLiteral[currentValue];
                return previousValue;
            }, {} as U);
    }

    public getNextGasDayStart(
        timestampInUnixMillis: number = Date.now()
    ): number {
        const subjectDate = moment(timestampInUnixMillis);
        const gasDayStartTime = angular
            .copy(subjectDate)
            .hour(6)
            .minute(0)
            .second(0)
            .millisecond(0);

        if (subjectDate.hour() >= 6) {
            gasDayStartTime.add({ day: 1 });
        }

        return gasDayStartTime.valueOf();
    }

    public getCurrentGasDayStart(
        timestampInUnixMillis: number = Date.now()
    ): number {
        return this.getNextGasDayStart(
            moment(timestampInUnixMillis).add(-1, 'days').toDate().getTime()
        );
    }

    public isGasDayEffective(
        timestampInUnixMillis: number = Date.now()
    ): boolean {
        const now = moment();
        const nextGasDayStartOfSubject: moment.Moment = moment(
            this.getNextGasDayStart(timestampInUnixMillis)
        );
        return (
            nextGasDayStartOfSubject.isBefore(now) ||
            nextGasDayStartOfSubject.isSame(now)
        );
    }

    public getRunTimeSuggestion(
        runtimePreSelection: RuntimePreSelection,
        timestampInUnixMillis: number = Date.now()
    ): DateInterval {
        let runtimeStart: moment.Moment;
        let runtimeEnd: Date;
        const now = moment(timestampInUnixMillis);

        if (runtimePreSelection === 'DAY') {
            runtimeStart = moment(this.getNextGasDayStart(now.valueOf()));
            runtimeEnd = moment(
                this.getNextGasDayStart(runtimeStart.valueOf())
            ).toDate();
        } else if (runtimePreSelection === 'MONTH') {
            runtimeStart = moment(
                this.getNextGasDayStart(
                    now.endOf('month').endOf('day').valueOf()
                )
            );
            runtimeEnd = moment(
                this.getNextGasDayStart(
                    angular
                        .copy(runtimeStart)
                        .endOf('month')
                        .endOf('day')
                        .valueOf()
                )
            ).toDate();
        }

        return <DateInterval>{
            start: runtimeStart.toDate(),
            end: runtimeEnd
        };
    }

    public toMoment(dateInterval: DateInterval): DateIntervalAsMoment {
        return {
            start: moment(dateInterval.start),
            end: moment(dateInterval.end)
        };
    }

    public toISOString(
        dateIntervalAsMoment: DateIntervalAsMoment
    ): DateInterval {
        return {
            start: moment(dateIntervalAsMoment.start).toISOString(),
            end: moment(dateIntervalAsMoment.end).toISOString()
        };
    }

    public scrollToAnchor(
        anchor: string,
        speed: number = 800,
        offsetTop: number = 80,
        timeout: number = 500
    ): void {
        this.$timeout(() => {
            $('html,body').animate(
                { scrollTop: $('#' + anchor).offset().top - offsetTop },
                speed
            );
        }, timeout);
    }

    public padLeadingZero(num: number): string {
        return num < 10 ? `0${num}` : num.toString();
    }

    /** Special about this: It works beyond 2038 */
    public isCentralEuropeanSummerTime(subjectDate: Date): boolean {
        if (subjectDate.getFullYear() < 2038) {
            return moment(subjectDate).isDST();
        }

        const fullYear = subjectDate.getFullYear();
        const january = new Date(fullYear, 0, 1);
        const july = new Date(fullYear, 6, 1);
        return (
            fullYear >= 2038 &&
            Math.min(january.getTimezoneOffset(), july.getTimezoneOffset()) ==
                subjectDate.getTimezoneOffset()
        );
    }

    /**
     * Test whether or not <code>value.value.value</code> or <code>value.originalValue.value</code> can be constructed into
     * a Decimal object.
     */
    public isValidDecimal(value: QuantityDto | PriceDto): boolean {
        try {
            new Decimal(value.value.value);
            return true;
        } catch (e) {
            try {
                new Decimal(value.originalValue.value);
                return true;
            } catch (e) {
                return false;
            }
        }
    }

    setTimeToStartOfGasDay(date: string | Date): Date {
        const dateAsDate: Date = new Date(date.valueOf());

        dateAsDate.setHours(6);
        dateAsDate.setMinutes(0);
        dateAsDate.setSeconds(0);
        dateAsDate.setMilliseconds(0);

        return dateAsDate;
    }

    setTimeToStartOfDay(date: string | Date): Date {
        const dateAsDate: Date = new Date(date.valueOf());

        dateAsDate.setHours(0);
        dateAsDate.setMinutes(0);
        dateAsDate.setSeconds(0);
        dateAsDate.setMilliseconds(0);

        return dateAsDate;
    }

    setTimeToEndOfDay(date: string | Date): Date {
        const dateAsDate: Date = new Date(date.valueOf());

        dateAsDate.setHours(23);
        dateAsDate.setMinutes(59);
        dateAsDate.setSeconds(59);
        dateAsDate.setMilliseconds(59);

        return dateAsDate;
    }

    /**
     * Generate a string that can be used as query parameters for the CSV and PDF exports.
     * <tt>$httpParamSerializerJQLike</tt> can't be used, because it doesn't support shallow serialization.
     *
     * @returns {string} The query param string for the URL. Does not prepend a question mark.
     */
    objectLiteralToQueryParamString(params: { [key: string]: any }) {
        const generatedFilterParams = params;
        let queryParamsWithoutEmptyValues = {};

        for (let obj in generatedFilterParams) {
            if (!generatedFilterParams.hasOwnProperty(obj)) {
                continue;
            }
            if (generatedFilterParams[obj] instanceof Date) {
                queryParamsWithoutEmptyValues[obj] =
                    generatedFilterParams[obj].toISOString();
            } else if (
                angular.isDefined(generatedFilterParams[obj]) &&
                generatedFilterParams[obj] !== null
            ) {
                queryParamsWithoutEmptyValues[obj] = generatedFilterParams[obj];
            }
        }

        return $.param(queryParamsWithoutEmptyValues, true);
    }

    validateProductRuntime(
        periodType: PeriodType,
        start: moment.Moment,
        end: moment.Moment
    ): boolean {
        const nextGasDayStart: moment.Moment = moment(
            this.getNextGasDayStart(Date.now())
        );
        const currentGasMonthStart: moment.Moment = moment()
            .startOf('month')
            .hour(6);

        if (start.minute() !== 0 || end.minute() !== 0) return false;

        if (periodType === 'WITHINDAY') {
            return (
                start.isAfter(this.getCurrentGasDayStart(Date.now())) &&
                start.hour() !== 6 &&
                end.isSame(
                    start
                        .clone()
                        .add(start.hour() < 6 ? 0 : 1, 'day')
                        .startOf('day')
                        .hour(6)
                )
            );
        } else if (periodType === 'DAY') {
            return (
                start.isSameOrAfter(nextGasDayStart) &&
                this.isGasDayStart(start) &&
                end.isSame(start.clone().add(1, 'day'))
            );
        } else if (periodType === 'MONTH') {
            return (
                this.isGasDayStart(start) &&
                start.isSame(start.clone().startOf('month').hour(6)) &&
                start.isSameOrAfter(currentGasMonthStart) &&
                this.isGasDayStart(end) &&
                end.isSame(end.clone().startOf('month').hour(6)) &&
                end.isSame(start.clone().add(1, 'month'))
            );
        } else if (periodType === 'QUARTER') {
            const quarterStarts = [0, 3, 6, 9];
            return (
                this.isGasDayStart(start) &&
                this.isGasDayStart(end) &&
                quarterStarts.indexOf(start.month()) !== -1 &&
                start.isSame(start.clone().startOf('month').hour(6)) &&
                end.isSame(end.clone().startOf('month').hour(6)) &&
                start.isSameOrAfter(currentGasMonthStart) &&
                end.isSame(start.clone().add(3, 'month'))
            );
        } else if (periodType === 'YEAR') {
            return (
                this.isGasDayStart(start) &&
                this.isGasDayStart(end) &&
                end.isSame(start.clone().add(1, 'year')) &&
                start.isSame(start.clone().startOf('month').hour(6)) &&
                end.isSame(end.clone().startOf('month').hour(6)) &&
                start.isSameOrAfter(currentGasMonthStart)
            );
        }

        return true;
    }

    private isGasDayStart(instance: moment.Moment) {
        return instance.isSame(instance.clone().startOf('day').hours(6));
    }

    public escapeHtml(unsafe) {
        return unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}

angular.module('app').service('utilService', UtilService);
